import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Typography from '@tiptap/extension-typography';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { common, createLowlight } from 'lowlight';
import { Paper, Box, Toolbar, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import CodeIcon from '@mui/icons-material/Code';
import TitleIcon from '@mui/icons-material/Title';
import LinkIcon from '@mui/icons-material/Link';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';

const lowlight = createLowlight(common);

const MarkdownEditor = ({ value, onChange, minHeight = 200 }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Typography,
      CodeBlockLowlight.configure({
        lowlight,
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html);
    },
  });

  if (!editor) {
    return null;
  }

  return (
    <Paper variant="outlined" sx={{ marginBottom: 2 }}>
      <Toolbar variant="dense" disableGutters sx={{ borderBottom: '1px solid rgba(0, 0, 0, 0.12)', px: 1 }}>
        <ToggleButtonGroup size="small">
          <Tooltip title="Bold">
            <ToggleButton
              value="bold"
              selected={editor.isActive('bold')}
              onClick={() => editor.chain().focus().toggleBold().run()}
            >
              <FormatBoldIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
          
          <Tooltip title="Italic">
            <ToggleButton
              value="italic"
              selected={editor.isActive('italic')}
              onClick={() => editor.chain().focus().toggleItalic().run()}
            >
              <FormatItalicIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>

          <Tooltip title="Heading">
            <ToggleButton
              value="heading"
              selected={editor.isActive('heading')}
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            >
              <TitleIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>

        <Box sx={{ mx: 0.5 }} />

        <ToggleButtonGroup size="small">
          <Tooltip title="Bullet List">
            <ToggleButton
              value="bulletList"
              selected={editor.isActive('bulletList')}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
            >
              <FormatListBulletedIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
          
          <Tooltip title="Numbered List">
            <ToggleButton
              value="orderedList"
              selected={editor.isActive('orderedList')}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
            >
              <FormatListNumberedIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>

        <Box sx={{ mx: 0.5 }} />
        
        <ToggleButtonGroup size="small">
          <Tooltip title="Code">
            <ToggleButton
              value="code"
              selected={editor.isActive('code')}
              onClick={() => editor.chain().focus().toggleCode().run()}
            >
              <CodeIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
          
          <Tooltip title="Quote">
            <ToggleButton
              value="blockquote"
              selected={editor.isActive('blockquote')}
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
            >
              <FormatQuoteIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>
      </Toolbar>
      
      <Box sx={{ p: 1 }}>
        <EditorContent 
          editor={editor} 
          style={{ 
            minHeight: minHeight,
            maxHeight: '500px',
            overflowY: 'auto'
          }} 
        />
      </Box>
    </Paper>
  );
};

export default MarkdownEditor;
